import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../services/api';
import '../styles/ListPages.css';
import '../styles/FormPages.css';

interface User {
  staffID: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  mfaEnabled?: boolean;
}

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState<string | null>(null);
  const [resettingMFA, setResettingMFA] = useState(false);

  // Form state (password removed - now auto-generated)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Success Coach',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiGet<User[]>('/auth/staff');

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch users');
      }

      setUsers(response.data || []);
    } catch (err: unknown) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = editingUser
        ? await apiPut<User>(`/auth/staff/${editingUser.staffID}`, formData)
        : await apiPost<User>('/auth/staff', formData);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to save user');
      }

      // Reset form and refresh users
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'Success Coach',
      });
      setShowAddForm(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
    });
    setShowAddForm(true);
  };

  const handleDeactivate = async (staffID: number) => {
    if (!confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      const response = await apiPut(`/auth/staff/${staffID}/deactivate`, {});

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to deactivate user');
      }

      fetchUsers();
    } catch (err: unknown) {
      setError('Failed to deactivate user');
      console.error(err);
    }
  };

  const handleReactivate = async (staffID: number) => {
    try {
      const response = await apiPut(`/auth/staff/${staffID}/reactivate`, {});

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to reactivate user');
      }

      fetchUsers();
    } catch (err: unknown) {
      setError('Failed to reactivate user');
      console.error(err);
    }
  };

  const handleSendPasswordReset = async (email: string, firstName: string, lastName: string) => {
    if (!confirm(`Send password reset email to ${firstName} ${lastName} (${email})?`)) {
      return;
    }

    setSendingPasswordReset(true);
    setError('');
    setPasswordResetSuccess(null);

    try {
      const response = await apiPost('/auth/forgot-password', { email });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to send password reset email');
      }

      setPasswordResetSuccess(`Password reset email sent to ${email}`);
      setTimeout(() => setPasswordResetSuccess(null), 5000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const handleResetMFA = async (staffID: number, firstName: string, lastName: string) => {
    if (
      !confirm(
        `Reset MFA for ${firstName} ${lastName}? They will need to re-configure their authenticator app on next login.`
      )
    ) {
      return;
    }

    setResettingMFA(true);
    setError('');
    setPasswordResetSuccess(null);

    try {
      // TODO: Implement backend endpoint for MFA reset
      const response = await apiPost(`/mfa/reset/${staffID}`, {});

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to reset MFA');
      }

      setPasswordResetSuccess(`MFA reset successfully for ${firstName} ${lastName}`);
      setTimeout(() => setPasswordResetSuccess(null), 5000);
      fetchUsers(); // Refresh to show updated MFA status
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setResettingMFA(false);
    }
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'Success Coach',
    });
    setError('');
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="list-page">
      <div className="page-header">
        <h2>User Management</h2>
        <p>Manage system users and permissions</p>
      </div>

      <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Link to="/admin/config">
          <button className="button-secondary">⚙️ System Configuration</button>
        </Link>
        <Link to="/admin/announcements">
          <button className="button-secondary">📢 Announcements</button>
        </Link>
        <Link to="/admin/audit-logs">
          <button className="button-secondary">📋 Audit Logs</button>
        </Link>
        <Link to="/admin/duplicates">
          <button className="button-secondary">🔍 Find Duplicates</button>
        </Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {passwordResetSuccess && (
        <div
          className="alert alert-success"
          style={{ background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724' }}
        >
          {passwordResetSuccess}
        </div>
      )}

      {!showAddForm && (
        <div className="list-actions">
          <button className="button-primary" onClick={() => setShowAddForm(true)}>
            Add New User
          </button>
        </div>
      )}

      {showAddForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">Role *</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Success Coach">Success Coach</option>
                    <option value="Supervisor">Supervisor</option>
                    <option value="Admin">Admin</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                </div>
              </div>

              {!editingUser && (
                <div
                  className="alert alert-info"
                  style={{
                    marginTop: '1rem',
                    background: '#E3F2FD',
                    border: '1px solid #2196F3',
                    padding: '1rem',
                    borderRadius: '4px',
                  }}
                >
                  <strong>ℹ️ Automatic Password Generation</strong>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#0D47A1' }}>
                    A temporary 8-character password will be automatically generated and sent to the
                    user via email. The user will be required to change this password on their first
                    login to a strong password (10+ characters with complexity requirements).
                  </p>
                </div>
              )}

              {editingUser && (
                <div
                  style={{
                    marginTop: '1.5rem',
                    padding: '1rem',
                    background: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                  }}
                >
                  <strong>🔑 Password Management</strong>
                  <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>
                    Need to reset this user&apos;s password? Send them a password reset email.
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      handleSendPasswordReset(formData.email, formData.firstName, formData.lastName)
                    }
                    disabled={sendingPasswordReset}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: '#ffc107',
                      color: '#000',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: sendingPasswordReset ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      opacity: sendingPasswordReset ? 0.6 : 1,
                    }}
                  >
                    {sendingPasswordReset ? '📧 Sending...' : '📧 Send Password Reset Email'}
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button type="submit" className="button-primary">
                {editingUser ? 'Update User' : 'Create User'}
              </button>
              <button type="button" onClick={cancelForm} className="button-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>MFA</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.staffID}>
                <td>
                  <strong>
                    {user.firstName} {user.lastName}
                  </strong>
                </td>
                <td>{user.email}</td>
                <td>
                  <span className={`badge badge-${user.role.toLowerCase().replace(' ', '-')}`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  {user.isActive ? (
                    <span style={{ color: '#388E3C', fontWeight: 600 }}>Active</span>
                  ) : (
                    <span style={{ color: '#D32F2F', fontWeight: 600 }}>Inactive</span>
                  )}
                </td>
                <td>
                  {user.mfaEnabled ? (
                    <span style={{ color: '#388E3C', fontWeight: 600 }}>✓ Enabled</span>
                  ) : (
                    <span style={{ color: '#9E9E9E' }}>Not Enabled</span>
                  )}
                </td>
                <td className="actions">
                  <button
                    className="button-link"
                    onClick={() => handleEdit(user)}
                    style={{ marginRight: '1rem' }}
                  >
                    Edit
                  </button>
                  {user.mfaEnabled && (
                    <button
                      className="button-link"
                      onClick={() => handleResetMFA(user.staffID, user.firstName, user.lastName)}
                      style={{ marginRight: '1rem', color: '#FF9800' }}
                      disabled={resettingMFA}
                    >
                      Reset MFA
                    </button>
                  )}
                  {user.isActive ? (
                    <button
                      className="button-link"
                      onClick={() => handleDeactivate(user.staffID)}
                      style={{ color: '#D32F2F' }}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      className="button-link"
                      onClick={() => handleReactivate(user.staffID)}
                      style={{ color: '#388E3C' }}
                    >
                      Reactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
